import { effect, signal } from "@preact-signals/safe-react";

import { getLocalStorage } from "@pdpp/lib-react";

// Main navigation tabs
export const MainTab = {
  Plan: "plan",
  ActivityCatalogue: "activityCatalogue",
  WorkloadDrivers: "workloadDrivers",
  Forecast: "forecast",
} as const;

type MainTabType = (typeof MainTab)[keyof typeof MainTab];

// Plan sub-tabs
export const PlanSubTab = {
  Overview: "overview",
  Plan: "plan",
  Activities: "activities",
  Methods: "methods",
  Adoption: "adoption",
} as const;

type PlanSubTabType = (typeof PlanSubTab)[keyof typeof PlanSubTab];

// Activity Catalogue sub-tabs
export const ActivityCatalogueSubTab = {
  ActivityList: "activityList",
  DepartmentMapping: "departmentMapping",
} as const;

type ActivityCatalogueSubTabType =
  (typeof ActivityCatalogueSubTab)[keyof typeof ActivityCatalogueSubTab];

// Tab state interface
interface TabState {
  readonly [MainTab.Plan]: PlanSubTabType;
  readonly [MainTab.ActivityCatalogue]: ActivityCatalogueSubTabType;
  readonly [MainTab.WorkloadDrivers]: string; // WLD doesn't have sub-tabs
  readonly [MainTab.Forecast]: string; // Forecast doesn't have sub-tabs
}

// Default tab states
const defaultTabState: TabState = {
  [MainTab.Plan]: PlanSubTab.Overview,
  [MainTab.ActivityCatalogue]: ActivityCatalogueSubTab.ActivityList,
  [MainTab.WorkloadDrivers]: "",
  [MainTab.Forecast]: "",
};

const storage = getLocalStorage();

const loadTabState = (): TabState => {
  try {
    const stored = storage["tabState"] as TabState | undefined;
    if (stored !== undefined && stored !== null) {
      return { ...defaultTabState, ...stored };
    }
  } catch (error) {
    console.warn("Failed to parse tabState from localStorage", error);
  }
  return defaultTabState;
};

// Create signal with initial state from localStorage
export const tabState$ = signal<TabState>(loadTabState());

effect(() => {
  const state = tabState$.value;
  try {
    storage["tabState"] = state;
  } catch (error) {
    console.warn("Failed to save tabState to localStorage", error);
  }
});

export const setMainTab = (mainTab: MainTabType, subTab: string): void => {
  tabState$.value = {
    ...tabState$.value,
    [mainTab]: subTab,
  };
};
